import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getNewsById, news } from "@/mocks/news";
import { getWorkById } from "@/mocks/works";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ShareButtons from "@/components/feature/ShareButtons";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  useSeo({
    title: item ? `${item.title} - 希岛爱理资讯` : "资讯未找到 - 希岛爱理资讯",
    description: item ? item.summary : "未找到对应的影视资讯。",
    keywords: item ? `${item.title},希岛爱理资讯,影视动态` : "希岛爱理资讯",
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <i className="ri-error-warning-line text-4xl text-foreground-300" />
        <h1 className="font-heading text-xl font-bold text-foreground-950 mt-4">未找到该资讯</h1>
        <p className="text-sm text-foreground-500 mt-2">资讯可能已被移除或地址有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = news.filter((n) => n.id !== item.id).slice(0, 3);
  const linkedWork = item.relatedWorkId ? getWorkById(item.relatedWorkId) : undefined;

  return (
    <div className="w-full pb-16">
      <Breadcrumbs
        items={[{ label: "首页", to: "/" }, { label: "资讯", to: "/news" }, { label: item.title }]}
      />

      <article className="w-full px-4 md:px-8 pt-6">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block text-xs font-label font-semibold tracking-[0.2em] uppercase text-accent-600 mb-3">
            News
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
            {item.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-foreground-500 mt-4">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              <time>{item.date}</time>
            </span>
          </div>

          <div className="w-full aspect-[3/2] rounded-lg overflow-hidden bg-background-200 mt-6">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`${item.title} - 希岛爱理资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((paragraph, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-700 leading-loose">
                {paragraph}
              </p>
            ))}
          </div>

          {linkedWork && (
            <div className="mt-8 rounded-lg bg-background-100 border border-background-200 p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="w-16 h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                <img
                  src={linkedWork.cover}
                  alt={`${linkedWork.title} 海报`}
                  title={`${linkedWork.title} - 希岛爱理影视作品`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1">
                <p className="text-xs text-foreground-500">相关作品</p>
                <h3 className="font-heading font-semibold text-foreground-950 mt-0.5">
                  《{linkedWork.title}》 · {linkedWork.year}
                </h3>
                <Link
                  to={`/works/${linkedWork.id}`}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看作品详情
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-background-200">
            <ShareButtons title={`${item.title} - 希岛爱理资讯`} />
          </div>
        </div>
      </article>

      <section className="w-full px-4 md:px-8 pt-14">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐资讯</h2>
          <div className="flex flex-col gap-3">
            {related.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="group flex items-center justify-between gap-4 rounded-md bg-background-100 border border-background-200 px-4 py-3 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="min-w-0">
                  <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{n.date}</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-accent-600 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}